import type { Entries } from './catalog';

/**
 * The copy of this interface's consequential actions and of what is not open to a person here.
 *
 * Each destructive or irreversible action states its consequence before anything happens — what is
 * lost, what is kept and how to undo it — accepts with its own verb, and reports its outcome with the
 * same verb in the past tense (decision D17). What a person may not do is explained in place, with who
 * can change it and a way on (decision D06); where nothing is refused, a quiet note says who can. Deleting
 * an account erases it; the organizations it leaves alone are closed, which keeps their records: the two
 * words are not interchangeable.
 */
export const actions: Record<'en' | 'es', Entries> = {
	en: {
		'members.remove.accept': 'Remove member',
		'members.remove.lost': 'Their membership of this organization and their role in it',
		'members.remove.kept': 'Their Beyond account, and what they did here, which stays readable',
		'members.remove.recovery': 'Send them a new invitation.',
		'members.remove.done': 'Member removed.',
		'members.role.done': 'Role changed.',

		'invitations.revoke.title': 'Withdraw the invitation to {email}?',
		'invitations.revoke.body': 'Its link stops working at once.',
		'invitations.revoke.accept': 'Withdraw invitation',
		'invitations.revoke.lost': 'The invitation link',
		'invitations.revoke.kept': 'A record of the invitation, marked as withdrawn',
		'invitations.revoke.recovery': 'Send a new invitation to the same address.',
		'invitations.revoke.done': 'Invitation withdrawn.',

		'settings.done': 'Organization renamed.',

		'identities.detach.title': 'Remove {provider} as a way to sign in?',
		'identities.detach.body': 'You can no longer sign in to this account with {provider}.',
		'identities.detach.accept': 'Remove sign-in method',
		'identities.detach.lost': 'Signing in with this {provider} identity',
		'identities.detach.kept': 'Your account, your organizations and your other ways to sign in',
		'identities.detach.recovery': 'Add it again from this page, after a recent sign-in.',
		'identities.detach.done': 'Sign-in method removed.',

		'sessions.everywhere.title': 'Sign out on all devices?',
		'sessions.everywhere.body': 'This ends your sessions in every product and device, including this browser.',
		'sessions.everywhere.lost': 'Every open session, this one included',
		'sessions.everywhere.kept': 'Your account, your organizations, your ways to sign in and the AI apps with access',
		'sessions.everywhere.recovery': 'Sign in again wherever you need to. Remove an app’s access in AI apps with access.',
		'sessions.everywhere.lost.access': 'Every open session, this one included, and the access of every AI app and access token',
		'sessions.everywhere.kept.access': 'Your account, your organizations and your ways to sign in',
		'sessions.everywhere.recovery.access': 'Sign in again wherever you need to, connect each app again from it and create new tokens.',

		'data.erase.lost.identity': 'Your name, your addresses and your ways to sign in',
		'data.erase.lost.sessions': 'Every session, in every product',
		'data.erase.lost.memberships': 'Your membership of every organization; organizations nobody else belongs to are closed',
		'data.erase.lost.connections': 'Every AI app with access, which stops acting for you now',
		'data.erase.kept': 'An anonymous record that this account was deleted, so its ways to sign in cannot bring it back',
		'data.erase.recovery': 'Sign in within 14 days and choose Keep my account.',
		'data.erase.working': 'Deleting your account…',
		'data.erase.blocking': 'You are the only owner of these organizations, which other people belong to. Make someone else an owner in each first:',
		'data.deletion': 'Your account will be deleted on {date}.',
		'data.deletion.keep': 'Keep my account',
		'data.deletion.kept': 'Your account will not be deleted.',

		'organization.closed.title': 'This organization is not open to you',
		'organization.closed.reason': 'It does not exist, or you are not one of its members.',
		'organization.closed.owner': 'Its owners and administrators, who can invite you',
		'organization.manage.title': 'Invitations, settings and activity are for owners and administrators',
		'organization.manage.reason': 'Your role here is {role}: you can see the members, but not invite people, change roles, rename the organization or read its activity.',
		'organization.manage.owner': 'An owner or administrator of {organization}',
		'organization.manage.action': 'See the members',
		'organization.manage.note': 'Owners and administrators invite people, change roles and remove members.'
	},
	es: {
		'members.remove.accept': 'Quitar miembro',
		'members.remove.lost': 'Su membresía de esta organización y su rol en ella',
		'members.remove.kept': 'Su cuenta de Beyond y lo que hizo aquí, que sigue siendo legible',
		'members.remove.recovery': 'Envíale una invitación nueva.',
		'members.remove.done': 'Miembro quitado.',
		'members.role.done': 'Rol cambiado.',

		'invitations.revoke.title': '¿Retirar la invitación a {email}?',
		'invitations.revoke.body': 'Su enlace deja de funcionar enseguida.',
		'invitations.revoke.accept': 'Retirar invitación',
		'invitations.revoke.lost': 'El enlace de la invitación',
		'invitations.revoke.kept': 'Un registro de la invitación, marcada como retirada',
		'invitations.revoke.recovery': 'Envía una invitación nueva a la misma dirección.',
		'invitations.revoke.done': 'Invitación retirada.',

		'settings.done': 'Organización renombrada.',

		'identities.detach.title': '¿Quitar {provider} como forma de iniciar sesión?',
		'identities.detach.body': 'Ya no podrás iniciar sesión en esta cuenta con {provider}.',
		'identities.detach.accept': 'Quitar forma de acceso',
		'identities.detach.lost': 'Iniciar sesión con esta identidad de {provider}',
		'identities.detach.kept': 'Tu cuenta, tus organizaciones y tus otras formas de iniciar sesión',
		'identities.detach.recovery': 'Vuelve a añadirla desde esta página, tras un inicio de sesión reciente.',
		'identities.detach.done': 'Forma de acceso quitada.',

		'sessions.everywhere.title': '¿Cerrar sesión en todos los dispositivos?',
		'sessions.everywhere.body': 'Esto cierra tus sesiones en todos los productos y dispositivos, incluido este navegador.',
		'sessions.everywhere.lost': 'Todas las sesiones abiertas, también esta',
		'sessions.everywhere.kept': 'Tu cuenta, tus organizaciones, tus formas de iniciar sesión y las apps de IA con acceso',
		'sessions.everywhere.recovery': 'Vuelve a iniciar sesión donde lo necesites. Quita el acceso de una app en Apps de IA con acceso.',
		'sessions.everywhere.lost.access': 'Todas las sesiones abiertas, también esta, y el acceso de cada app de IA y token de acceso',
		'sessions.everywhere.kept.access': 'Tu cuenta, tus organizaciones y tus formas de iniciar sesión',
		'sessions.everywhere.recovery.access': 'Vuelve a iniciar sesión donde lo necesites, conecta de nuevo cada app desde ella y crea tokens nuevos.',

		'data.erase.lost.identity': 'Tu nombre, tus direcciones y tus formas de iniciar sesión',
		'data.erase.lost.sessions': 'Todas tus sesiones, en todos los productos',
		'data.erase.lost.memberships': 'Tu membresía en cada organización; las organizaciones a las que no pertenece nadie más se cierran',
		'data.erase.lost.connections': 'Todas las apps de IA con acceso, que dejan de actuar por ti desde ahora',
		'data.erase.kept': 'Un registro anónimo de que esta cuenta se eliminó, para que sus formas de acceso no puedan recuperarla',
		'data.erase.recovery': 'Inicia sesión en los próximos 14 días y elige Conservar mi cuenta.',
		'data.erase.blocking': 'Eres el único propietario de estas organizaciones, a las que pertenecen otras personas. Primero haz propietario a otra persona en cada una:',
		'data.deletion': 'Tu cuenta se eliminará el {date}.',
		'data.deletion.keep': 'Conservar mi cuenta',
		'data.deletion.kept': 'Tu cuenta no se eliminará.',
		'data.erase.working': 'Eliminando tu cuenta…',

		'organization.closed.title': 'Esta organización no está abierta para ti',
		'organization.closed.reason': 'No existe o no eres uno de sus miembros.',
		'organization.closed.owner': 'Sus propietarios y administradores, que pueden invitarte',
		'organization.manage.title': 'Las invitaciones, los ajustes y la actividad son para propietarios y administradores',
		'organization.manage.reason': 'Tu rol aquí es {role}: puedes ver a los miembros, pero no invitar, cambiar roles, renombrar la organización ni leer su actividad.',
		'organization.manage.owner': 'Quien sea propietario o administrador de {organization}',
		'organization.manage.action': 'Ver los miembros',
		'organization.manage.note': 'Los propietarios y administradores invitan a personas, cambian roles y quitan miembros.'
	}
};
